/**
 * usePublishContent Hook
 *
 * Orchestration hook used by the MonetizationStep publish action. Mirrors the
 * Flutter `content_monetization_screen.dart:_save` flow
 * (lib/presentation/screens/publish/content_monetization_screen.dart:193):
 *
 * 1. Derive confirm title/description based on contentSettings (publish /
 *    schedule / draft).
 * 2. Await ConfirmSheet — return early if cancelled.
 * 3. Dispatch startUpload, invoke PublishContentUseCase, dispatch endUpload.
 * 4. On upload error → `toast.error` with the upload error message and
 *    dismiss the overlay. Transcode runs in the background inside the service;
 *    any transcode failure is reflected on the media item itself.
 * 5. On success (upload kicked off) → call onFinish so the sheet closes.
 *
 * The optional `onUploaded` seam reports the publish result back to the sheet's
 * opener — the web equivalent of Flutter's awaited
 * `NavigationService.navigateToAddContentScreen(...) -> bool uploaded`
 * (lib/presentation/bottom_sheet/comments_bottom_sheet.dart:915). See the
 * contract on the param itself.
 */

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { appLogger } from '@/domain/services/analytics/appLogger';
import {
  startUpload,
  setProgress,
  endUpload,
} from '@/presentation/store/slices/uploadGlobalOverlayProgress/uploadGlobalOverlayProgressSlice';
import { publishContentUseCase } from '@/domain/usecases/publish/publishContentUseCase';
import { savePendingProcessingMediaUseCase } from '@/domain/usecases/userMedia/savePendingProcessingMediaUseCase';
import { clearPendingProcessingMediaUseCase } from '@/domain/usecases/userMedia/clearPendingProcessingMediaUseCase';
import { buildLocalUserMedia } from '@/domain/utils/localUserMediaBuilder';
import type { RootState } from '@/presentation/store';
import type { ContentReplyCommentInfoEntity } from '@/domain/entities/comment/contentReplyCommentInfoEntity';
import type { VideoMeta } from './useAddContent';
import type { DescriptionResult } from './useContentDescription';
import type { ContentSettingsResult } from './useContentSettings';
import type { MonetizationResult } from './useContentMonetization';

interface UsePublishContentParams {
  mediaId: string;
  selectedFile: File | null;
  videoMeta: VideoMeta | null;
  descriptionResult: DescriptionResult | null;
  contentSettings: ContentSettingsResult | null;
  onFinish: () => void;
  screenKey?: string;
  /**
   * Set only by the comment-reply flow; rides the `transcodeUserVideo` payload
   * so the server can stamp the real `mediaInfo` onto the comment doc. Passed
   * straight through to the request, never derived — Flutter forwards
   * `widget.contentReplyCommentInfo` verbatim into `uploadMedia`
   * (lib/presentation/screens/publish/content_monetization_screen.dart:241-253).
   */
  contentReplyCommentInfo?: ContentReplyCommentInfoEntity | null;
  /**
   * Reports the publish result to the sheet's opener — the web equivalent of
   * Flutter's `bool uploaded` pop value
   * (lib/presentation/screens/publish/content_monetization_screen.dart:117-119
   * → lib/presentation/bottom_sheet/comments_bottom_sheet.dart:915).
   *
   * Contract: fires **at most once**, **only when the publish was confirmed**,
   * and reports upload **kick-off — not upload success**. Flutter's
   * `AppStorageService.uploadUserMedia` fires the upload+transcode un-awaited
   * and returns `true` immediately
   * (lib/services/app_storage_service.dart:48-61: "do not wait for the upload
   * and transcode / pop and show a progress overlay"), so the upload outcome
   * must never gate this callback; a genuine upload failure is surfaced later
   * by its own toast (`app_storage_service.dart:124-134`), by which time the
   * opener has already acted on `uploaded == true`. The two pre-upload
   * bail-outs and a cancelled confirm are Flutter's never-pops paths (`_save`
   * returns without popping), so they do not fire it at all.
   *
   * The `(success: boolean)` parameter IS Flutter's `bool uploaded`; it is
   * simply always `true` on the confirmed path today, exactly as on Flutter.
   */
  onUploaded?: (success: boolean) => void;
}

export const usePublishContent = ({
  mediaId,
  selectedFile,
  videoMeta,
  descriptionResult,
  contentSettings,
  onFinish,
  screenKey,
  contentReplyCommentInfo,
  onUploaded,
}: UsePublishContentParams) => {
  const dispatch = useDispatch();
  const { l10n } = useLocale();
  const userName = useSelector((state: RootState) => state.auth.user?.username ?? null);
  const firebaseUserId = useSelector((state: RootState) => state.auth.firebaseUserId);

  const publish = useCallback(async (monetizationResult: MonetizationResult) => {
    if (!selectedFile || !videoMeta || !descriptionResult || !contentSettings) {
      appLogger.error(
        'Missing required data to publish',
        'usePublishContent.publish',
      );
      return;
    }

    if (!firebaseUserId) {
      toast.error(l10n.publish.publishError);
      return;
    }

    const { title, description } = (() => {
      if (contentSettings.isDraft) {
        return {
          title: l10n.publish.confirmDraftTitle,
          description: l10n.publish.confirmDraftDescription,
        };
      }
      if (contentSettings.schedule != null) {
        return {
          title: l10n.publish.confirmScheduleTitle,
          description: l10n.publish.confirmScheduleDescription,
        };
      }
      return {
        title: l10n.publish.confirmPublishTitle,
        description: l10n.publish.confirmPublishDescription,
      };
    })();

    let confirmed;
    try {
      confirmed = await ConfirmSheet.show({
        title,
        confirmText: description,
        okText: l10n.publish.confirmYes,
        cancelText: l10n.publish.confirmCancel,
        screenKey,
      });
    } catch (error) {
      appLogger.error('Failed to show confirm sheet', 'usePublishContent.publish', { error: error as Error });
      toast.error(l10n.publish.publishError);
      return;
    }

    if (!confirmed) return;

    dispatch(startUpload({ message: l10n.publish.uploadingVideo }));
    // Close the sheet immediately once the upload has been confirmed —
    // matches Flutter's uploadUserMedia returning before the inner pipeline.
    onFinish();

    const publishRequest = {
      mediaId,
      file: selectedFile,
      videoMeta,
      descriptionResult,
      contentSettings,
      monetizationResult,
      userName,
      firebaseUserId,
      contentReplyCommentInfo: contentReplyCommentInfo ?? null,
    };

    // Persist optimistic entity so the grid shows Processing immediately;
    // cleared in finally if the upload fails (no server doc would arrive).
    const localMedia = buildLocalUserMedia(publishRequest, '');
    try {
      await savePendingProcessingMediaUseCase.execute(localMedia);
    } catch (error) {
      appLogger.error(
        'Failed to save pending processing media',
        'usePublishContent.publish', { error: error as Error },
      );
    }

    // Report kick-off to the opener BEFORE awaiting the upload — Flutter's
    // `uploadUserMedia` returns `true` the moment the background upload is
    // fired (app_storage_service.dart:48-61) and the screen pops with that
    // value, so the opener (e.g. the comments sheet) acts immediately. Kept
    // OUT of the `finally` on purpose: the upload outcome must not gate it.
    onUploaded?.(true);

    let uploadSucceeded = false;
    try {
      const result = await publishContentUseCase.execute(
        publishRequest,
        {
          onProgress: (percent) => dispatch(setProgress(percent)),
        },
      );
      uploadSucceeded = result.success;
      if (!result.success) {
        toast.error(l10n.publish.uploadError);
      }
    } catch (error) {
      appLogger.error('Publish failed', 'usePublishContent.publish', { error: error as Error });
      toast.error(l10n.publish.uploadError);
    } finally {
      dispatch(endUpload());
      if (!uploadSucceeded) {
        // Drop the optimistic local tile — Firestore will never receive a doc
        // for this id, so the live listener could never clear it on its own.
        try {
          await clearPendingProcessingMediaUseCase.execute(mediaId);
        } catch (error) {
          appLogger.error(
            'Failed to clear pending processing media after upload failure',
            'usePublishContent.publish', { error: error as Error },
          );
        }
      }
    }
  }, [selectedFile, videoMeta, descriptionResult, contentSettings, mediaId, userName, firebaseUserId, dispatch, l10n, onFinish, screenKey, contentReplyCommentInfo, onUploaded]);

  return { publish };
};
